// src/components/SuccessStep.tsx
import React, { useState } from 'react';
import { CheckCircle, ArrowRight, AlertTriangle } from 'lucide-react';

interface SuccessStepProps {
  processedData: {
    accountsProcessed: number;
    periodsDetected: number;
    filesProcessed: number;
    signConventionWarnings?: number;
    formatInfo?: Array<{
      type: string;
      layout: string;
      confidence: number;
      signConvention: string;
    }>;
    warnings?: Array<{
      message: string;
      severity: string;
    }>;
  } | {
    accountEntries: any[];
    statistics: {
      accountsProcessed: number;
      periodsDetected: number;
      filesProcessed: number;
      signConventionWarnings?: number;
      formatInfo?: Array<{
        type: string;
        layout: string;
        confidence: number;
        signConvention: string;
      }>;
      warnings?: Array<{
        message: string;
        severity: string;
      }>;
    };
  };
  detectedPeriodType?: 'monthly' | 'quarterly' | 'annual'; // NEW: Period type for display
  onContinue: () => void;
}

export const SuccessStep: React.FC<SuccessStepProps> = ({ 
  processedData, 
  detectedPeriodType = 'monthly',
  onContinue
}) => {
  // Extract statistics from processedData
  const statistics = 'statistics' in processedData ? processedData.statistics : processedData;
  const [showFormatDetails, setShowFormatDetails] = useState(false);
  
  // Helper function to get period label based on detected type
  const getPeriodLabel = (periodType: 'monthly' | 'quarterly' | 'annual'): string => {
    switch (periodType) {
      case 'quarterly':
        return 'Quarters Detected';
      case 'annual':
        return 'Years Detected';
      default:
        return 'Months Detected';
    }
  };
  
  // Debug: Log format detection results to console
  if (statistics.formatInfo && statistics.formatInfo.length > 0) {
    console.log('[SuccessStep] Format Detection Results:', statistics.formatInfo);
  }
  return (
    <div className="max-w-4xl mx-auto text-center">
      <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
      <h2 className="text-3xl font-bold text-gray-900 mb-3">Data Successfully Processed!</h2>
      <p className="text-lg text-gray-600 mb-8">
        Your financial data has been uploaded and is ready for analysis.
      </p>

      <div className="bg-green-50 border border-green-200 rounded-lg p-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-700 mb-2">
              {statistics.accountsProcessed}
            </div>
            <div className="text-sm font-medium text-green-600 uppercase tracking-wide">
              Unique Accounts Uploaded
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-green-700 mb-2">
              {statistics.periodsDetected}
            </div>
            <div className="text-sm font-medium text-green-600 uppercase tracking-wide">
              {getPeriodLabel(detectedPeriodType)}
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-3xl font-bold text-green-700 mb-2">
              {statistics.filesProcessed}
            </div>
            <div className="text-sm font-medium text-green-600 uppercase tracking-wide">
              {statistics.filesProcessed === 1 ? 'File' : 'Files'} Processed
            </div>
          </div>
        </div>
      </div>

      {/* Format Detection Results - REMOVED FROM UI, NOW IN CONSOLE LOG */}

      {/* Warnings if any */}
      {statistics.warnings && statistics.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
          <div className="flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-600 mr-2" />
            <h3 className="text-lg font-semibold text-yellow-800">Processing Warnings</h3>
          </div>
          <ul className="text-left space-y-2">
            {statistics.warnings.map((warning, index) => (
              <li key={index} className="text-sm text-yellow-700 flex items-start">
                <span className="w-2 h-2 bg-yellow-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                {warning.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-center">
        <button
          onClick={onContinue}
          disabled={statistics.accountsProcessed === 0}
          className={`inline-flex items-center px-8 py-4 rounded-lg transition-colors text-lg font-semibold shadow-md ${
            statistics.accountsProcessed === 0 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          Start Analyzing Data
          <ArrowRight className="w-5 h-5 ml-2" />
        </button>
      </div>
      
      {statistics.accountsProcessed === 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center justify-center mb-2">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">No Financial Data Processed</span>
          </div>
          <p className="text-sm text-red-700 text-center">
            No accounts were extracted from your files. Please check your file format and ensure it contains financial data.
          </p>
        </div>
      )}

      <p className="text-sm text-gray-500 mt-6">
        You can now categorize accounts, review financial statements, and analyze your data.
      </p>
    </div>
  );
};